import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import {
  LayoutDashboard,
  MapPin,
  Building2,
  Activity,
  TrendingUp,
  Gauge,
  Database,
  Radio,
  Wrench,
  FileText,
  Ticket,
  AlertTriangle,
  FileBarChart,
  BarChart3,
  Calendar,
  Settings,
  Bell,
  Search,
  Zap,
  Menu,
  X,
  CloudSun,
} from 'lucide-react';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notifications] = useState(5);

  const menuSections = [
    {
      title: 'Overview',
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
        { icon: MapPin, label: 'State View', path: '/state-view' },
        { icon: Building2, label: 'Site Directory', path: '/site-directory' },
      ],
    },
    {
      title: 'Plant Monitoring',
      items: [
        { icon: Activity, label: 'Performance Metrics', path: '/performance' },
        { icon: TrendingUp, label: 'Generation & Capacity', path: '/generation' },
        { icon: CloudSun, label: 'Weather', path: '/weather' },
      ],
    },
    {
      title: 'RMS Integration',
      items: [
        { icon: Radio, label: 'Devices & Status', path: '/rms-devices' },
        { icon: Database, label: 'API Health & Sync', path: '/rms-health' },
      ],
    },
    {
      title: 'Operations',
      items: [
        { icon: Wrench, label: 'Preventive Maintenance', path: '/maintenance' },
        { icon: Ticket, label: 'Consumer Complaints', path: '/complaints' },
        { icon: AlertTriangle, label: 'Escalations', path: '/escalations' },
      ],
    },
    {
      title: 'Reports',
      items: [
        { icon: FileBarChart, label: 'MIS Reports', path: '/reports' },
        { icon: BarChart3, label: 'Site Comparison', path: '/site-comparison' },
      ],
    },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f5f7fa' }}>
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-white border-b z-50 shadow-sm">
        <div className="h-full px-6 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden"
            >
              {sidebarOpen ? <X /> : <Menu />}
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#10ac84' }}>
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{ color: '#0a3d62' }}>BREDA BMS</h1>
                <p className="text-xs" style={{ color: '#576574' }}>Building Management System</p>
              </div>
            </div>
            <Badge variant="outline" className="px-3 py-1" style={{ backgroundColor: '#fff4e6', color: '#f97316', borderColor: '#f97316' }}>
              DEMO / PROTOTYPE
            </Badge>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden md:block relative" style={{ width: '320px' }}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#576574' }} />
              <Input
                placeholder="Search sites, districts, tokens..."
                className="pl-10"
              />
            </div>
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="w-5 h-5" />
              {notifications > 0 && (
                <span className="absolute top-1 right-1 w-5 h-5 rounded-full text-xs flex items-center justify-center text-white" style={{ backgroundColor: '#e74c3c' }}>
                  {notifications}
                </span>
              )}
            </Button>
            <Avatar className="cursor-pointer" onClick={() => navigate('/login')}>
              <AvatarFallback style={{ backgroundColor: '#0a3d62', color: 'white' }}>AU</AvatarFallback>
            </Avatar>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-16 bottom-0 w-64 bg-white border-r overflow-y-auto transition-transform duration-300 z-40 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="p-4 space-y-6">
          {menuSections.map((section, idx) => (
            <div key={idx}>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#576574' }}>
                {section.title}
              </h3>
              <div className="space-y-1">
                {section.items.map((item, itemIdx) => (
                  <Button
                    key={itemIdx}
                    variant="ghost"
                    className={`w-full justify-start gap-3 h-11 ${
                      isActive(item.path)
                        ? 'font-semibold'
                        : ''
                    }`}
                    style={{
                      backgroundColor: isActive(item.path) ? '#e8f5f1' : 'transparent',
                      color: isActive(item.path) ? '#10ac84' : '#576574',
                    }}
                    onClick={() => navigate(item.path)}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Button>
                ))}
              </div>
              {idx < menuSections.length - 1 && (
                <div className="h-px my-4" style={{ backgroundColor: '#e5e7eb' }} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`transition-all duration-300 pt-16 ${
          sidebarOpen ? 'lg:pl-64' : ''
        }`}
      >
        <div className="p-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Layout;
